import { useMemo } from "react";

import { SignalHookScopeType } from "../SignalFormPage/types";
import { bindingToHook } from "../SignalFormPage/utils";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { Signal } from "@/queries/signals";

const BINDINGS_GAP_PX = 8;
const OVERFLOW_INDICATOR_WIDTH_PX = 30;

const describeBinding = (binding: Signal["bindings"][number]): string => {
  const hook = bindingToHook(binding);
  if (hook.scopeType === SignalHookScopeType.ALL_REQUESTS) {
    return "All requests";
  }
  if (hook.scopeType === SignalHookScopeType.PROMPT_LABEL) {
    return `Labels: ${hook.labelNames.join(", ")}`;
  }
  if (hook.variantType === "version" && hook.versionNumber !== null) {
    return `${hook.promptName ?? "Prompt"} (v${hook.versionNumber})`;
  }
  if (hook.variantType === "label" && hook.versionLabel) {
    return `${hook.promptName ?? "Prompt"} (${hook.versionLabel})`;
  }
  return hook.promptName ?? "Specific prompt template";
};

const renderBindingBadge = (label: string) => (
  <span className="shrink-0 whitespace-nowrap rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">
    {label}
  </span>
);

interface SignalCardBindingsProps {
  bindings: Signal["bindings"];
}

const SignalCardBindings = ({ bindings }: SignalCardBindingsProps) => {
  const bindingItems = useMemo(
    () =>
      bindings.map(binding => ({
        id: binding.id,
        label: describeBinding(binding),
      })),
    [bindings],
  );
  const recalcKey = bindingItems.map(item => `${item.id}:${item.label}`).join("|");
  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: bindingItems.length,
    gapPx: BINDINGS_GAP_PX,
    overflowIndicatorWidth: OVERFLOW_INDICATOR_WIDTH_PX,
    keepMinOne: true,
    recalcKey,
  });

  if (bindingItems.length === 0) {
    return null;
  }

  const visibleItems = bindingItems.slice(0, visibleCount);
  const hiddenItems = bindingItems.slice(visibleCount);
  const hiddenTooltipContent = hiddenItems.map(item => item.label).join(", ");

  return (
    <div
      ref={containerRef}
      className="relative flex w-full min-w-0 max-w-full flex-nowrap items-center gap-2 overflow-hidden"
    >
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute flex flex-nowrap items-center gap-2"
        aria-hidden
      >
        {bindingItems.map(item => (
          <span key={item.id}>{renderBindingBadge(item.label)}</span>
        ))}
      </div>
      {visibleItems.map(item => (
        <span key={item.id}>{renderBindingBadge(item.label)}</span>
      ))}
      {hiddenCount > 0 && (
        <BasicTooltip content={hiddenTooltipContent}>
          <span className="inline-block shrink-0 whitespace-nowrap px-1 text-xs text-gray-500">
            +{hiddenCount}
          </span>
        </BasicTooltip>
      )}
    </div>
  );
};

export default SignalCardBindings;
